import { FleetAreaCode } from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { FleetAreasPanel } from '@/products/fleet/features/fleet-areas-panel';
import { fleetPanelClassName } from '@/products/fleet/features/fleet-panel';
import { useFleetPolicy, useUpdateFleetPolicy } from '@/products/fleet/features/use-fleet';
import { fleetAreaEnabled, useFleetAreas } from '@/products/fleet/features/use-fleet-areas';

type Draft = {
  licenseCheckIntervalMonths: string;
  instructionIntervalMonths: string;
  reminderDaysLegal: string;
  reminderDaysContract: string;
  mileageStaleDays: string;
  maxDailyKmWarning: string;
  allowLicensePhoto: boolean;
  licensePhotoRequired: boolean;
  licensePhotoRetentionMonths: string;
  telematicsStaleHours: string;
  tripRetentionMonths: string;
  solarWebUrl: string;
  weatherLatitude: string;
  weatherLongitude: string;
};

export function FleetPolicyPage() {
  usePageTitle('Fuhrpark-Richtlinie');
  const policy = useFleetPolicy();
  const areas = useFleetAreas();
  const save = useUpdateFleetPolicy();
  const instructionOn = fleetAreaEnabled(areas.data, FleetAreaCode.INSTRUCTION) === true;
  const telematicsOn = fleetAreaEnabled(areas.data, FleetAreaCode.TELEMATICS) === true;
  const [draft, setDraft] = useState<Draft | null>(null);
  const values: Draft = draft ?? {
    licenseCheckIntervalMonths: String(policy.data?.licenseCheckIntervalMonths ?? ''),
    instructionIntervalMonths: String(policy.data?.instructionIntervalMonths ?? ''),
    reminderDaysLegal: String(policy.data?.reminderDaysLegal ?? ''),
    reminderDaysContract: String(policy.data?.reminderDaysContract ?? ''),
    mileageStaleDays: String(policy.data?.mileageStaleDays ?? ''),
    maxDailyKmWarning: String(policy.data?.maxDailyKmWarning ?? ''),
    allowLicensePhoto: policy.data?.allowLicensePhoto ?? false,
    licensePhotoRequired: policy.data?.licensePhotoRequired ?? true,
    licensePhotoRetentionMonths: String(policy.data?.licensePhotoRetentionMonths ?? ''),
    telematicsStaleHours: String(policy.data?.telematicsStaleHours ?? 72),
    tripRetentionMonths: String(policy.data?.tripRetentionMonths ?? 24),
    solarWebUrl: policy.data?.solarWebUrl ?? '',
    weatherLatitude: showCoord(policy.data?.weatherLatitude),
    weatherLongitude: showCoord(policy.data?.weatherLongitude),
  };

  function patch(next: Partial<Draft>) {
    setDraft((current) => ({ ...(current ?? values), ...next }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const weatherLatitude = parseCoord(values.weatherLatitude);
    const weatherLongitude = parseCoord(values.weatherLongitude);
    if (Number.isNaN(weatherLatitude) || Number.isNaN(weatherLongitude)) {
      toast.error('Breitengrad und Längengrad bitte als Zahl angeben.');
      return;
    }
    try {
      await save.mutateAsync({
        licenseCheckIntervalMonths: Number(values.licenseCheckIntervalMonths),
        instructionIntervalMonths: Number(values.instructionIntervalMonths),
        reminderDaysLegal: Number(values.reminderDaysLegal),
        reminderDaysContract: Number(values.reminderDaysContract),
        mileageStaleDays: Number(values.mileageStaleDays),
        maxDailyKmWarning: Number(values.maxDailyKmWarning),
        allowLicensePhoto: values.allowLicensePhoto,
        licensePhotoRequired: values.licensePhotoRequired,
        licensePhotoRetentionMonths: Number(values.licensePhotoRetentionMonths),
        telematicsEnabled:
          fleetAreaEnabled(areas.data, FleetAreaCode.TELEMATICS) ??
          policy.data?.telematicsEnabled ??
          false,
        telematicsStaleHours: Number(values.telematicsStaleHours),
        tripRetentionMonths: Number(values.tripRetentionMonths),
        solarWebUrl: values.solarWebUrl.trim() || null,
        weatherLatitude,
        weatherLongitude,
      });
      setDraft(null);
      toast.success('Richtlinie gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Richtlinie konnte nicht gespeichert werden.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Richtlinie"
        description="Intervalle für Kontrolle, Unterweisung und Erinnerungen."
      />
      {policy.isPending ? (
        <p className="text-sm text-muted-foreground">Richtlinie wird geladen …</p>
      ) : null}
      {policy.isError ? (
        <p className="text-sm text-destructive">
          {policy.error instanceof ApiRequestError
            ? policy.error.message
            : 'Die Richtlinie konnte nicht geladen werden.'}
        </p>
      ) : null}
      {policy.data?.missing ? (
        <p className="text-sm text-muted-foreground">
          Die Richtlinie ist noch nicht gespeichert. Die Werte sind der Standard.
        </p>
      ) : null}
      <FleetAreasPanel />
      <form
        className={`${fleetPanelClassName} space-y-4`}
        onSubmit={(event) => void onSubmit(event)}
      >
        <div className="space-y-3">
          <p className="text-sm font-medium">Photovoltaik</p>
          <p className="text-sm text-muted-foreground">
            Öffentlicher Solar.web-Link oder der Compare-Link derselben Anlage. Für die Prognose den
            Standort als Breiten- und Längengrad, zum Beispiel Höxter 51,775 und 9,381.
          </p>
          <div className="space-y-1">
            <Label htmlFor="solar-web-url">Solar.web-Adresse</Label>
            <Input
              id="solar-web-url"
              value={values.solarWebUrl}
              onChange={(event) => patch({ solarWebUrl: event.target.value })}
              placeholder="https://www.solarweb.com/PublicDisplay/PvSystem?token=…"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="weather-latitude">Breitengrad</Label>
              <Input
                id="weather-latitude"
                inputMode="decimal"
                value={values.weatherLatitude}
                onChange={(event) => patch({ weatherLatitude: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="weather-longitude">Längengrad</Label>
              <Input
                id="weather-longitude"
                inputMode="decimal"
                value={values.weatherLongitude}
                onChange={(event) => patch({ weatherLongitude: event.target.value })}
              />
            </div>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <NumberField
            label="Führerscheinkontrolle (Monate)"
            value={values.licenseCheckIntervalMonths}
            onChange={(value) => patch({ licenseCheckIntervalMonths: value })}
          />
          {instructionOn ? (
            <NumberField
              label="UVV-Unterweisung (Monate)"
              value={values.instructionIntervalMonths}
              onChange={(value) => patch({ instructionIntervalMonths: value })}
            />
          ) : null}
          <NumberField
            label="Erinnerung Fristen (Tage)"
            value={values.reminderDaysLegal}
            onChange={(value) => patch({ reminderDaysLegal: value })}
          />
          <NumberField
            label="Erinnerung Verträge (Tage)"
            value={values.reminderDaysContract}
            onChange={(value) => patch({ reminderDaysContract: value })}
          />
          <NumberField
            label="Kilometerstand veraltet nach (Tage)"
            value={values.mileageStaleDays}
            onChange={(value) => patch({ mileageStaleDays: value })}
          />
          <NumberField
            label="Warnung ab Kilometern"
            value={values.maxDailyKmWarning}
            onChange={(value) => patch({ maxDailyKmWarning: value })}
          />
          <NumberField
            label="Führerschein-Foto behalten (Monate)"
            value={values.licensePhotoRetentionMonths}
            onChange={(value) => patch({ licensePhotoRetentionMonths: value })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch
            checked={values.allowLicensePhoto}
            onCheckedChange={(checked) => patch({ allowLicensePhoto: checked })}
          />
          Führerschein-Foto erlauben
        </label>
        {values.allowLicensePhoto ? (
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={values.licensePhotoRequired}
              onCheckedChange={(checked) => patch({ licensePhotoRequired: checked })}
            />
            Führerschein-Foto ist Pflicht
          </label>
        ) : null}
        {telematicsOn ? (
          <div className={`${fleetPanelClassName} space-y-3`}>
            <p className="text-sm font-medium">Telematik</p>
            <p className="text-sm text-muted-foreground">
              Gespeichert werden der letzte Standort des Fahrzeugs und Fahrten ohne Wegstrecke.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <NumberField
                label="Kein Signal nach (Stunden)"
                value={values.telematicsStaleHours}
                onChange={(value) => patch({ telematicsStaleHours: value })}
              />
              <NumberField
                label="Fahrten aufbewahren (Monate)"
                value={values.tripRetentionMonths}
                onChange={(value) => patch({ tripRetentionMonths: value })}
              />
            </div>
          </div>
        ) : null}
        <Button type="submit" disabled={save.isPending || policy.isPending}>
          Speichern
        </Button>
      </form>
    </div>
  );
}

function showCoord(value: number | null | undefined): string {
  if (value == null) return '';
  return value.toLocaleString('de-DE', { maximumFractionDigits: 6, useGrouping: false });
}

function parseCoord(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = label.replace(/\s+/g, '-').toLowerCase();
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="mt-2"
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
